/**
 * useUpload
 * 
 * @Author: Focci
 * @Date: 2023-08-16 17:13:43
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-16 17:13:43
 */

import PropTypes from 'prop-types'
import { getBase64Items, getFiles } from '@comp/upload/lib'
import { useCallback, useEffect, useState } from 'react'
import { map, filter, isFunction, isObject } from 'lodash-es'

/**
 * ## fetcher返回一个Promise的函数
 *  - file
 *  - controller(new AbortController()) 里面的 signal 用于取消请求，而向接口传的参数 
 * 
 *  eg:
 * 
 *  fetcher({ file, controller }) {
 *    const { url: image } = file
 *    const { signal } = controller
 *    return fetchPost('user/note/uploadImage', { image }, { signal })
 *  }
 * 
 * 
 * ## successCallback 接口上传成功后，会调用此函数，返回的值必须为一个对象
 *    且里面需要包括一个url，用来替换上传成功后的url
 * 
 */
export default function useUpload(
  fetcher, 
  successCallback,
  option = {}
) {
  const needBase64 = option.needBase64 || true
  const value = option.value || []
  const multiple = option.multiple === undefined ? true : option.multiple
  const limit = option.limit || 9

  const [allFiles, setFiles] = useState(value)
  const [compelete, setCompelete] = useState(false) // 是否上传完成

  const changeData = useCallback((data, i) => {
    setFiles((prev) => {
      const tmp = map(prev, (item, index) => {
        if(index === i) {
          return { ...item, ...data }
        }
        return item
      })
      return [...tmp]
    })
  }, [])

  const handleStartUpload = useCallback(async (files) => {
    const res = needBase64 
      ? await getBase64Items(files) 
      : getFiles(files)
    
    setFiles((prev) => {
      if(!multiple) {
        return [...res]
      }

      const names = map(prev, 'name')
      const newItems = filter(res, (f) => names.indexOf(f.name) === -1)
      
      return [...prev, ...newItems].slice(0, limit)
    })
  }, [limit, multiple, needBase64])

  const uploadCallback = useCallback((file, controller) => {
    const { name } = file
    const i = map(allFiles, 'name').indexOf(name)
    
    changeData({ status: 'uploading' }, i)
    fetcher({ file, controller })
      .then((r) => {
        const tmp = isFunction(successCallback) ? successCallback(r) : null
        changeData({ 
          status: 'finish',
          ...(isObject(tmp) ? tmp : {})
        }, i)
      })
      .catch(() => {
        changeData({ status: 'error' }, i)
      })
  }, [changeData, fetcher, allFiles, successCallback])

  const removeIndex = useCallback((index) => {
    setFiles((prev) => {
      const tmp = [...prev]
      tmp.splice(index, 1)
      return tmp
    })
  }, [])

  // 将指定索引设置封面
  const setCover = useCallback((index) => {
    setFiles((prev) => {
      const tmp = [...prev]
      tmp.unshift(tmp.splice(index, 1)?.[0])
      return tmp
    })
  }, [])

  // 设置一个默认值
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFiles(value) }, [])

  // 通过检测状态，来判断是否全部上传完成
  useEffect(() => {
    const tmp = filter(allFiles, (f) => ['finish', 'error'].indexOf(f.status) > -1)
    const len = tmp.length
    if(len > 0 && len === allFiles.length) {
      setCompelete(true)
    }
  }, [allFiles])

  return {
    compelete,
    files: allFiles,
    removeIndex,
    setCover,
    handleStartUpload,
    uploadCallback
  }
}

useUpload.propTypes = {
  fetcher: PropTypes.func.isRequired,
  successCallback: PropTypes.func.isRequired,
  option: PropTypes.array,
}
